import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color gradient for funnel stages
const stageColors = [
  '#3b82f6', // QR Scan - Blue
  '#06b6d4', // Landing View - Cyan
  '#8b5cf6', // CTA Click - Purple
  '#a855f7', // Signup Started - Violet
  '#22c55e', // Signup Completed - Green
  '#f59e0b', // Gift Redeemed - Amber
  '#ec4899', // Reading Started - Pink
  '#f43f5e', // Reading Completed - Rose
  '#14b8a6', // Story Shared - Teal
];

const stageIcons = ['📱', '👁️', '👆', '📝', '✅', '🎁', '🔮', '✨', '📤'];

// Funnel Visualization Component - Extracted for performance
const FunnelVisualization = ({ data }) => {
  if (!data || !data.stages || data.stages.length === 0) {
    return (
      <div style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '20px',
        padding: '64px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '56px', marginBottom: '20px' }}>📊</div>
        <h3 style={{ margin: '0 0 10px', color: 'white', fontSize: '18px', fontWeight: '600' }}>Henüz funnel verisi yok</h3>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
          QR taramaları başladığında funnel verileri görünecek
        </p>
      </div>
    );
  }

  const maxCount = Math.max(...data.stages.map(s => s.count), 1);

  return (
    <div style={{ marginBottom: '32px' }}>
      {/* Demo Data Badge */}
      {data.is_demo_data && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          background: 'rgba(251, 191, 36, 0.15)',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          borderRadius: '10px',
          padding: '10px 18px',
          marginBottom: '20px',
          color: '#fbbf24',
          fontSize: '13px',
          fontWeight: '500'
        }}>
          <span>⚠️</span>
          <span>Demo Verisi Gösteriliyor</span>
        </div>
      )}
      
      {/* Premium Overall Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '20px',
        marginBottom: '28px'
      }}>
        <div style={{
          background: 'linear-gradient(145deg, rgba(34,197,94,0.12) 0%, rgba(34,197,94,0.04) 100%)',
          border: '1px solid rgba(34,197,94,0.2)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#22c55e', marginBottom: '4px' }}>
            {data.overall_conversion}%
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
            Genel Dönüşüm
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(145deg, rgba(239,68,68,0.12) 0%, rgba(239,68,68,0.04) 100%)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#ef4444', marginBottom: '4px' }}>
            {data.total_dropoff}
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
            Toplam Kayıp
          </div>
        </div>
        <div style={{
          background: 'linear-gradient(145deg, rgba(59,130,246,0.12) 0%, rgba(59,130,246,0.04) 100%)',
          border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: '16px',
          padding: '24px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '36px', fontWeight: '700', color: '#3b82f6', marginBottom: '4px' }}>
            {data.stages[0]?.count || 0}
          </div>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>
            Toplam Giriş
          </div>
        </div>
      </div>

      {/* Premium Funnel Bars */}
      <div style={{
        background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px',
        padding: '28px',
        overflow: 'hidden'
      }}>
        {data.stages.map((stage, index) => {
          const barWidth = (stage.count / maxCount) * 100;
          const isLast = index === data.stages.length - 1;
          
          return (
            <motion.div
              key={stage.key}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{ marginBottom: isLast ? 0 : '24px' }}
            >
              {/* Stage Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <span style={{ fontSize: '24px' }}>{stageIcons[index]}</span>
                  <div>
                    <div style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>
                      {stage.label}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
                      Aşama {index + 1}
                    </div>
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: 'white', fontWeight: '700', fontSize: '20px' }}>
                    {stage.count.toLocaleString()}
                  </div>
                  <div style={{ 
                    color: stage.step_conversion >= 50 ? '#22c55e' : 
                           stage.step_conversion >= 20 ? '#fbbf24' : '#ef4444',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {index === 0 ? '—' : `${stage.step_conversion}% önceki adımdan`}
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div style={{
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '10px',
                height: '36px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${barWidth}%` }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                  style={{
                    background: `linear-gradient(90deg, ${stageColors[index]}90, ${stageColors[index]}60)`,
                    height: '100%',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '14px',
                    minWidth: stage.count > 0 ? '70px' : '0'
                  }}
                >
                  {stage.count > 0 && (
                    <span style={{ 
                      color: 'white', 
                      fontWeight: '600', 
                      fontSize: '13px',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}>
                      {stage.overall_conversion}%
                    </span>
                  )}
                </motion.div>
              </div>
              
              {/* Dropoff indicator */}
              {stage.dropoff > 0 && index > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '6px',
                  marginTop: '6px',
                  color: '#ef4444',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  <span>↓</span>
                  <span>{stage.dropoff.toLocaleString()} kullanıcı ayrıldı</span>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const AnalyticsPage = () => {
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('funnel'); // 'funnel' or 'overview'
  const [showDemoData, setShowDemoData] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, [showDemoData]);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch overview
      const overviewRes = await axios.get(`${API_URL}/api/growth-hub/analytics/overview`, { headers });
      setOverview(overviewRes.data);
      
      // Fetch funnel
      const funnelRes = await axios.get(
        `${API_URL}/api/growth-hub/analytics/funnel${showDemoData ? '?is_demo=true' : ''}`, 
        { headers }
      );
      setFunnel(funnelRes.data.funnel);
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.6)' }}>Yükleniyor...</div>;
  }

  const totals = overview?.totals || {};

  return (
    <div>
      {/* Premium Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '28px' 
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', letterSpacing: '-0.3px' }}>Analitik Merkezi</h2>
          <p style={{ margin: '6px 0 0', color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Partner performansı ve dönüşüm metrikleri
          </p>
        </div>
        
        {/* Premium View Toggle */}
        <div style={{ 
          display: 'flex', 
          gap: '4px',
          background: 'rgba(0,0,0,0.25)',
          padding: '4px',
          borderRadius: '12px'
        }}>
          <button
            onClick={() => setActiveView('funnel')}
            style={{
              padding: '10px 20px',
              background: activeView === 'funnel' ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(99, 102, 241, 0.2))' : 'transparent',
              border: 'none',
              borderRadius: '10px',
              color: activeView === 'funnel' ? '#c084fc' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeView === 'funnel' ? '600' : '500',
              transition: 'all 0.2s ease'
            }}
          >
            Funnel
          </button>
          <button
            onClick={() => setActiveView('overview')}
            style={{
              padding: '10px 20px',
              background: activeView === 'overview' ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.25), rgba(99, 102, 241, 0.2))' : 'transparent',
              border: 'none',
              borderRadius: '10px',
              color: activeView === 'overview' ? '#c084fc' : 'rgba(255,255,255,0.5)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: activeView === 'overview' ? '600' : '500',
              transition: 'all 0.2s ease'
            }}
          >
            Genel Bakış
          </button>
        </div>
      </div>

      {/* Demo Data Toggle - Only show for funnel view */}
      {activeView === 'funnel' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px',
          padding: '12px 16px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '13px'
          }}>
            <input
              type="checkbox"
              checked={showDemoData}
              onChange={(e) => setShowDemoData(e.target.checked)}
              style={{ 
                width: '16px', 
                height: '16px',
                accentColor: '#8b5cf6'
              }}
            />
            Demo verilerini dahil et
          </label>
          <span style={{ 
            color: 'rgba(255,255,255,0.4)', 
            fontSize: '12px',
            fontStyle: 'italic'
          }}>
            (Espressolab demo verileri)
          </span>
        </div>
      )}

      {/* Content based on active view */}
      {activeView === 'funnel' ? (
        <FunnelVisualization data={funnel} />
      ) : (
        <>
          {/* Premium Top KPI Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(5, 1fr)', 
            gap: '20px', 
            marginBottom: '36px' 
          }}>
            {[
              { label: 'Partnerler', value: totals?.total_partners || 0, icon: '🏪', color: '#8b5cf6', bgGradient: 'rgba(139,92,246,0.15)' },
              { label: 'QR Okutma', value: totals?.total_qr_scans || 0, icon: '📱', color: '#3b82f6', bgGradient: 'rgba(59,130,246,0.15)' },
              { label: 'Görüntülenme', value: totals?.total_landing_views || 0, icon: '👁️', color: '#06b6d4', bgGradient: 'rgba(6,182,212,0.15)' },
              { label: 'Kayıt', value: totals?.total_signups || 0, icon: '✅', color: '#22c55e', bgGradient: 'rgba(34,197,94,0.15)' },
              { label: 'Gelir', value: `₺${totals?.total_revenue || 0}`, icon: '💰', color: '#fbbf24', bgGradient: 'rgba(251,191,36,0.15)' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                style={{
                  background: `linear-gradient(145deg, ${stat.bgGradient} 0%, rgba(255,255,255,0.02) 100%)`,
                  border: `1px solid ${stat.color}20`,
                  borderRadius: '20px',
                  padding: '24px 20px',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ 
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  fontSize: '80px',
                  opacity: 0.08
                }}>{stat.icon}</div>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>{stat.icon}</div>
                <div style={{ fontSize: '32px', fontWeight: '700', color: stat.color, marginBottom: '6px' }}>{stat.value}</div>
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.5)', fontWeight: '500' }}>{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Top Partners by Signups */}
          {overview?.top_by_signups && overview.top_by_signups.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: 'rgba(255,255,255,0.8)' }}>
                🏆 En Çok Kayıt Getiren Partnerler
              </h3>
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                overflow: 'hidden'
              }}>
                {overview.top_by_signups.slice(0, 5).map((partner, i) => (
                  <div
                    key={partner.partner_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '16px 20px',
                      borderBottom: i < overview.top_by_signups.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none'
                    }}
                  >
                    <div style={{
                      width: '32px',
                      height: '32px',
                      background: i === 0 ? '#fbbf24' : i === 1 ? '#9ca3af' : i === 2 ? '#cd7f32' : 'rgba(255,255,255,0.1)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: i < 3 ? '#000' : '#fff',
                      marginRight: '16px'
                    }}>
                      {i + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: 'white', fontWeight: '500' }}>{partner.partner_name}</div>
                      <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                        {partner.landing_views} görüntülenme • %{partner.conversion_rate} dönüşüm
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '18px' }}>{partner.signup_completed}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>kayıt</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Partners Table */}
          {overview?.all_partners && overview.all_partners.length > 0 && (
            <div>
              <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: 'rgba(255,255,255,0.8)' }}>
                📋 Tüm Partner Performansı
              </h3>
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '16px',
                overflow: 'auto'
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <th style={{ padding: '14px 16px', textAlign: 'left', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>Partner</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>QR Okutma</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>Görüntülenme</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>Kayıt</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>Dönüşüm</th>
                      <th style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>İlk Fal</th>
                      <th style={{ padding: '14px 16px', textAlign: 'right', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '600' }}>Gelir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overview.all_partners.map((partner, i) => (
                      <tr 
                        key={partner.partner_id}
                        style={{ 
                          borderBottom: i < overview.all_partners.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                          background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)'
                        }}
                      >
                        <td style={{ padding: '14px 16px', color: 'white', fontWeight: '500' }}>{partner.partner_name}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>{partner.qr_scans}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>{partner.landing_views}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: '#22c55e', fontWeight: '600' }}>{partner.signup_completed}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: partner.conversion_rate > 10 ? '#22c55e' : 'rgba(255,255,255,0.7)' }}>
                          {partner.conversion_rate}%
                        </td>
                        <td style={{ padding: '14px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>{partner.first_readings}</td>
                        <td style={{ padding: '14px 16px', textAlign: 'right', color: '#f59e0b', fontWeight: '600' }}>₺{partner.total_revenue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsPage;
